// AuthModalContext — F8.7b · global open/close state for the AuthModal.
//
// Any component may call `openModal(tab, { prefillEmail, onClose })` to
// show the modal on top. State is intentionally minimal: `open`, `tab`,
// `prefillEmail`. An optional per-open `onClose` callback (FASE I
// Round-4) fires exactly once when the user dismisses the modal (ESC,
// backdrop click, X, or programmatic `closeModal()`); callers use it to
// react to the explicit dismissal event rather than inferring it from
// state transitions (which race against the mount cycle).
//
// Consumers reach into the context via `useAuthModal()`.
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";

const AuthModalContext = createContext(null);

export function AuthModalProvider({ children }) {
    const [state, setState] = useState({ open: false, tab: "login", prefillEmail: "" });
    // Per-open onClose callback. Kept in a ref so repeated openModal calls
    // never land in React's batched state updates (would clash with ESC
    // handlers that fire during the same frame as mount).
    const onCloseRef = useRef(null);

    const openModal = useCallback((tab = "login", opts = {}) => {
        onCloseRef.current = typeof opts.onClose === "function" ? opts.onClose : null;
        setState({ open: true, tab, prefillEmail: opts.prefillEmail || "" });
    }, []);

    const closeModal = useCallback(() => {
        setState((s) => ({ ...s, open: false }));
        // Fire the per-open onClose exactly once, then clear it so
        // subsequent openModal() cycles start fresh.
        const cb = onCloseRef.current;
        onCloseRef.current = null;
        if (cb) {
            try { cb(); } catch (_err) { /* swallow — modal must still close */ }
        }
    }, []);

    const setTab = useCallback((tab) => {
        setState((s) => ({ ...s, tab }));
    }, []);

    const value = useMemo(
        () => ({ ...state, openModal, closeModal, setTab }),
        [state, openModal, closeModal, setTab],
    );

    return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>;
}

export function useAuthModal() {
    const ctx = useContext(AuthModalContext);
    if (!ctx) throw new Error("useAuthModal must be used inside AuthModalProvider");
    return ctx;
}
